import type { BalanceCardItem } from '../components/panels/components/domain-sections/BalanceCardsSection';
import type { EventData } from '@shared/types/events.types';
import type { TransactionData } from '../components/panels/components/domain-sections/TransactionsSection';
import type { PaymentDetail } from '../components/panels/types/panel-common.types';
import type { RiskCheck } from '@shared/types/risk.types';
import type { BaseTimelineEvent } from '../components/panels/components/domain-sections/Timeline/types';

export const ECOMMERCE_BALANCE_DATA: BalanceCardItem[] = [
  {
    id: 'sales',
    label: 'Sales Revenue',
    totalBalance: '$847,329.50',
    balances: [
      { currency: 'USD', amount: 487652.30, formatted: '$487,652.30' },
      { currency: 'EUR', amount: 198743.85, formatted: '€198,743.85' },
      { currency: 'GBP', amount: 124876.12, formatted: '£124,876.12' },
      { currency: 'CAD', amount: 36057.23, formatted: 'CA$36,057.23' }
    ],
    status: 'sales',
    bgColor: 'bg-green-100',
    textColor: 'text-green-950'
  },
  {
    id: 'inventory',
    label: 'Inventory Value',
    totalBalance: '$293,847.75',
    balances: [
      { currency: 'USD', amount: 156239.45, formatted: '$156,239.45' },
      { currency: 'EUR', amount: 89342.18, formatted: '€89,342.18' },
      { currency: 'GBP', amount: 48266.12, formatted: '£48,266.12' }
    ],
    status: 'inventory',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-900'
  },
  {
    id: 'marketing',
    label: 'Marketing Budget',
    totalBalance: '$78,450.00',
    balances: [
      { currency: 'USD', amount: 45280.00, formatted: '$45,280.00' },
      { currency: 'EUR', amount: 21830.00, formatted: '€21,830.00' },
      { currency: 'GBP', amount: 11340.00, formatted: '£11,340.00' }
    ],
    status: 'marketing',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-900'
  },
  {
    id: 'customer_credits',
    label: 'Customer Credits',
    totalBalance: '$142,638.25',
    balances: [
      { currency: 'USD', amount: 89742.50, formatted: '$89,742.50' },
      { currency: 'EUR', amount: 34582.75, formatted: '€34,582.75' },
      { currency: 'GBP', amount: 18313.00, formatted: '£18,313.00' }
    ],
    status: 'customer_credits',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-900'
  }
];

export const ECOMMERCE_TIMELINE_DATA: BaseTimelineEvent[] = [
  {
    id: 'order-placed',
    title: 'New order placed',
    timestamp: '12/15/2024 14:23',
    details: [
      { action: 'Order #ECM-2024-0847 created', timestamp: '12/15/2024 14:23' },
      { action: 'Payment authorized', timestamp: '12/15/2024 14:23' },
      { action: 'Inventory reserved', timestamp: '12/15/2024 14:24' }
    ]
  },
  {
    id: 'product-launch',
    title: 'New product launched',
    timestamp: '12/15/2024 10:00',
    details: [
      { action: 'Product "Winter Collection 2024" published', timestamp: '12/15/2024 10:00' },
      { action: 'Marketing campaign activated', timestamp: '12/15/2024 10:01' }
    ]
  },
  {
    id: 'inventory-restock',
    title: 'Inventory restocked',
    timestamp: '12/15/2024 09:15',
  },
  {
    id: 'marketing-campaign',
    title: 'Email campaign sent',
    timestamp: '12/15/2024 08:30',
    details: [
      { action: 'Campaign "Holiday Special" sent to 15,847 customers', timestamp: '12/15/2024 08:30' },
      { action: 'Open rate: 24.3%', timestamp: '12/15/2024 09:45' },
      { action: 'Click rate: 8.7%', timestamp: '12/15/2024 10:15' }
    ]
  },
  {
    id: 'customer-review',
    title: 'Customer review received',
    timestamp: '12/14/2024 19:42',
  },
  {
    id: 'refund-processed',
    title: 'Refund processed',
    timestamp: '12/14/2024 16:18',
    details: [
      { action: 'Refund request approved', timestamp: '12/14/2024 15:30' },
      { action: 'Refund of $89.99 initiated', timestamp: '12/14/2024 16:18' }
    ]
  },
  {
    id: 'inventory-alert',
    title: 'Low stock alert',
    timestamp: '12/14/2024 13:07',
    details: [
      { action: 'Product SKU-WJ-2024 below threshold', timestamp: '12/14/2024 13:07' },
      { action: 'Reorder notification sent to supplier', timestamp: '12/14/2024 13:08' }
    ]
  },
  {
    id: 'payment-failed',
    title: 'Payment failed',
    timestamp: '12/14/2024 11:35',
    details: [
      { action: 'Subscription payment declined', timestamp: '12/14/2024 11:35' },
      { action: 'Customer notification sent', timestamp: '12/14/2024 11:36' }
    ]
  },
  {
    id: 'customer-support',
    title: 'Support ticket resolved',
    timestamp: '12/14/2024 10:22',
  },
  {
    id: 'abandoned-cart',
    title: 'Cart abandonment reminder sent',
    timestamp: '12/13/2024 18:45',
    details: [
      { action: 'Abandoned cart detected after 2 hours', timestamp: '12/13/2024 16:45' },
      { action: 'Recovery email sent', timestamp: '12/13/2024 18:45' }
    ]
  },
  {
    id: 'subscription-started',
    title: 'New subscription activated',
    timestamp: '12/13/2024 15:28',
  },
  {
    id: 'order-shipped',
    title: 'Order shipped',
    timestamp: '12/13/2024 12:14',
    details: [
      { action: 'Package handed to carrier', timestamp: '12/13/2024 12:14' },
      { action: 'Tracking number: TR-2024-ECM-9847', timestamp: '12/13/2024 12:14' },
      { action: 'Customer notification sent', timestamp: '12/13/2024 12:15' }
    ]
  },
  {
    id: 'promotion-applied',
    title: 'Promotional discount applied',
    timestamp: '12/13/2024 09:33',
  },
  {
    id: 'customer-registered',
    title: 'New customer registered',
    timestamp: '12/12/2024 20:17',
    details: [
      { action: 'Account created for sarah.johnson@email.com', timestamp: '12/12/2024 20:17' },
      { action: 'Welcome email sent', timestamp: '12/12/2024 20:18' }
    ]
  },
  {
    id: 'fraud-alert',
    title: 'Fraud check triggered',
    timestamp: '12/12/2024 17:52',
    details: [
      { action: 'Unusual transaction pattern detected', timestamp: '12/12/2024 17:52' },
      { action: 'Transaction flagged for manual review', timestamp: '12/12/2024 17:52' },
      { action: 'Security team notified', timestamp: '12/12/2024 17:53' }
    ]
  },
  {
    id: 'bulk-order',
    title: 'Large bulk order received',
    timestamp: '12/12/2024 14:08',
    details: [
      { action: 'Order #ECM-2024-0834 for 250 units', timestamp: '12/12/2024 14:08' },
      { action: 'Special pricing applied', timestamp: '12/12/2024 14:09' }
    ]
  },
  {
    id: 'return-processed',
    title: 'Product return processed',
    timestamp: '12/12/2024 11:41',
    details: [
      { action: 'Return package received', timestamp: '12/12/2024 10:30' },
      { action: 'Quality check completed', timestamp: '12/12/2024 11:15' },
      { action: 'Refund initiated', timestamp: '12/12/2024 11:41' }
    ]
  },
  {
    id: 'loyalty-reward',
    title: 'Loyalty points awarded',
    timestamp: '12/11/2024 16:25',
  },
  {
    id: 'seasonal-sale',
    title: 'Seasonal sale started',
    timestamp: '12/11/2024 00:00',
    details: [
      { action: 'Holiday sale pricing activated', timestamp: '12/11/2024 00:00' },
      { action: 'Sale banners deployed', timestamp: '12/10/2024 23:45' }
    ]
  },
  {
    id: 'inventory-audit',
    title: 'Monthly inventory audit completed',
    timestamp: '12/10/2024 17:30',
  }
];

export const ECOMMERCE_EVENTS_DATA: EventData[] = [
  {
    id: 'evt_ec_001',
    eventType: 'order.created',
    description: 'New order #3847 created for $1,249.99 via Shopify checkout',
    timestamp: '2024-09-22 14:32:18',
    requestId: 'req_7x9K2mN4pQ',
    status: 'succeeded'
  },
  {
    id: 'evt_ec_002',
    eventType: 'payment.captured',
    description: 'Payment of $1,249.99 successfully captured for order #3847',
    timestamp: '2024-09-22 14:32:20',
    requestId: 'req_8L3P5vW7rT',
    status: 'approved'
  },
  {
    id: 'evt_ec_003',
    eventType: 'inventory.updated',
    description: 'Stock levels updated for SKU-A1234, SKU-B5678 after order #3847',
    timestamp: '2024-09-22 14:32:22',
    requestId: 'req_2M6Q8xY9sU',
    status: 'succeeded'
  },
  {
    id: 'evt_ec_004',
    eventType: 'shipping.label_created',
    description: 'Shipping label generated for order #3847 via FedEx Express',
    timestamp: '2024-09-22 14:45:10',
    requestId: 'req_4N7R1zA3vW',
    status: 'verified'
  },
  {
    id: 'evt_ec_005',
    eventType: 'webhook.failed',
    description: 'Failed to send order confirmation webhook to CRM system',
    timestamp: '2024-09-22 14:45:15',
    requestId: 'req_9P2S4bC5xY',
    status: 'failed'
  },
  {
    id: 'evt_ec_006',
    eventType: 'cart.abandoned',
    description: 'Cart ID #C9283 abandoned with total value $450.00',
    timestamp: '2024-09-22 13:15:00',
    requestId: 'req_6T3U5dE7gZ',
    status: 'pending'
  },
  {
    id: 'evt_ec_007',
    eventType: 'refund.initiated',
    description: 'Refund of $89.99 initiated for order #3821',
    timestamp: '2024-09-22 12:00:05',
    requestId: 'req_1V4W6fG8hA',
    status: 'processing'
  },
  {
    id: 'evt_ec_008',
    eventType: 'customer.created',
    description: 'New customer account created for john.doe@example.com',
    timestamp: '2024-09-22 11:45:30',
    requestId: 'req_5X6Y7iJ9kB',
    status: 'succeeded'
  },
  {
    id: 'evt_ec_009',
    eventType: 'product.out_of_stock',
    description: 'Product "Premium Wireless Headphones" is now out of stock',
    timestamp: '2024-09-22 10:30:00',
    requestId: 'req_3Z8A9jL1mC',
    status: 'verified'
  },
  {
    id: 'evt_ec_010',
    eventType: 'sync.completed',
    description: 'Full catalog sync completed with 2,847 products updated',
    timestamp: '2024-09-22 09:00:00',
    requestId: 'req_7B2C3kN4oD',
    status: 'succeeded'
  }
];

export const ECOMMERCE_TRANSACTION_DATA: TransactionData[] = [
  {
    id: 'txn-001',
    risk: 12,
    transactionId: 'ORD-2024-8901',
    type: 'Payment',
    amount: '$3,456.78',
    paymentMethod: 'Visa •••• 4242',
    customer: 'Tech Solutions Inc',
    date: 'Dec 22, 2024'
  },
  {
    id: 'txn-002',
    risk: 8,
    transactionId: 'ORD-2024-8902',
    type: 'Payment',
    amount: '$1,299.00',
    paymentMethod: 'PayPal',
    customer: 'Global Retail Co',
    date: 'Dec 21, 2024'
  },
  {
    id: 'txn-003',
    risk: 35,
    transactionId: 'REF-2024-2301',
    type: 'Refund',
    amount: '-$549.99',
    paymentMethod: 'Mastercard •••• 5555',
    customer: 'Digital Marketing LLC',
    date: 'Dec 21, 2024'
  },
  {
    id: 'txn-004',
    risk: 5,
    transactionId: 'ORD-2024-8903',
    type: 'Payment',
    amount: '$8,750.00',
    paymentMethod: 'Bank Transfer',
    customer: 'Enterprise Corp',
    date: 'Dec 20, 2024'
  },
  {
    id: 'txn-005',
    risk: 15,
    transactionId: 'ORD-2024-8904',
    type: 'Payment',
    amount: '$425.50',
    paymentMethod: 'American Express •••• 3000',
    customer: 'Small Business Store',
    date: 'Dec 20, 2024'
  },
  {
    id: 'txn-006',
    risk: 72,
    transactionId: 'CHB-2024-0145',
    type: 'Chargeback',
    amount: '-$2,100.00',
    paymentMethod: 'Visa •••• 8888',
    customer: 'International Traders',
    date: 'Dec 19, 2024'
  },
  {
    id: 'txn-007',
    risk: 10,
    transactionId: 'ORD-2024-8905',
    type: 'Payment',
    amount: '$6,234.00',
    paymentMethod: 'Stripe Checkout',
    customer: 'Fashion Boutique',
    date: 'Dec 19, 2024'
  },
  {
    id: 'txn-008',
    risk: 18,
    transactionId: 'ORD-2024-8906',
    type: 'Payment',
    amount: '$975.25',
    paymentMethod: 'Apple Pay',
    customer: 'Mobile Commerce Ltd',
    date: 'Dec 18, 2024'
  },
  {
    id: 'txn-009',
    risk: 25,
    transactionId: 'REF-2024-2302',
    type: 'Refund',
    amount: '-$325.00',
    paymentMethod: 'Google Pay',
    customer: 'Online Marketplace',
    date: 'Dec 18, 2024'
  },
  {
    id: 'txn-010',
    risk: 7,
    transactionId: 'ORD-2024-8907',
    type: 'Payment',
    amount: '$15,890.00',
    paymentMethod: 'Wire Transfer',
    customer: 'Wholesale Distributors',
    date: 'Dec 17, 2024'
  }
];

export const ECOMMERCE_PROFILE_DETAILS: PaymentDetail[] = [
  { label: 'Limited use', value: 'Enabled - Max 5 uses per link' },
  { label: 'Allow promotion codes', value: 'Yes' },
  { label: 'Collect addresses', value: 'Billing and Shipping' },
  { label: 'Collect phone numbers', value: 'Required' },
  { label: 'Allow business customers to provide tax IDs', value: 'Enabled' },
  { label: 'Save payment details for future use', value: 'With customer consent' },
  { label: 'Collect tax automatically', value: 'Yes - Based on location' },
  { label: 'Collect terms of service agreement', value: 'Required before payment' },
  { label: 'Confirmation page', value: 'Custom branded page' },
  { label: 'Deactivated link page', value: 'Default message' },
  { label: 'Call to action button', value: 'Complete Purchase' },
  { label: 'Post-payment invoice', value: 'Auto-generated PDF' },
  { label: 'Created', value: 'Sep 22, 2025 09:30 AM' },
  { label: 'Expires', value: 'Dec 31, 2025 11:59 PM' }
];

export const ECOMMERCE_RISK_CHECKS: RiskCheck[] = [
  {
    status: 'success',
    title: 'Platform Integration',
    description: 'Shopify integration verified and active with proper API credentials',
    score: 0
  },
  {
    status: 'error',
    title: 'Payment Gateway Configuration',
    description: 'Multiple payment gateways detected without proper fallback configuration. This could lead to failed transactions',
    score: 35
  },
  {
    status: 'warning',
    title: 'Inventory Sync',
    description: 'Inventory levels not synchronized in the last 24 hours. Manual reconciliation may be required',
    score: 20
  },
  {
    status: 'error',
    title: 'Fraud Detection Rules',
    description: 'No fraud detection rules configured. High risk of fraudulent transactions',
    score: 45
  },
  {
    status: 'warning',
    title: 'Shipping Configuration',
    description: 'Some shipping zones have incomplete rate configurations',
    score: 15
  }
];